import { useEffect, useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { Calendar, UserCheck, Syringe, Pill, Lock, X, Delete, Eye, LogOut } from 'lucide-react';
import { useCurrentUser, useUnlockCounter, useLogout } from '@/api/auth';

/**
 * POSTE COMPTOIR — l'écran que toute la pharmacie a sous les yeux.
 *
 * Trois onglets, rien d'autre : l'emploi du temps, l'organisation du jour et
 * les rendez-vous. Aucun mot de passe pour y arriver — le titulaire a installé
 * ce poste une fois pour toutes, et l'ordinateur reste dessus.
 *
 * Le cadenas, en bas à droite et volontairement discret, est pour LUI : son PIN
 * ouvre la gestion sur cette même machine, le temps qu'il lui faut.
 *
 * Tout est dessiné plus grand qu'ailleurs dans l'application : cet écran se
 * consulte debout, à un mètre, souvent en tenant autre chose.
 *
 * Le même cadre sert l'ACCÈS EN CONSULTATION (Réglages → Accès consultation) :
 * un titulaire venu de PharmaGestion, dont un autre gère le RH. Son compte
 * porte une adresse, celui de la machine non. Lui n'a pas de cadenas — le PIN
 * ne lui ouvrirait rien — mais une déconnexion, qui le ramène au hub.
 */

const tabs = [
  { to: '/planning', label: 'Emploi du temps', icon: Calendar },
  { to: '/organization', label: 'Organisation du jour', icon: UserCheck },
  { to: '/appointments', label: 'Rendez-vous', icon: Syringe },
];

function PinPad({ onClose }: { onClose: () => void }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const unlock = useUnlockCounter();

  function press(digit: string) {
    if (pin.length >= 4 || unlock.isPending) return;
    setError(null);
    const next = pin + digit;
    setPin(next);
    if (next.length === 4) {
      unlock.mutate(
        { pin: next },
        {
          onError: (err) => {
            setError(
              (err as { message?: string }).message ?? 'Code PIN invalide'
            );
            setPin('');
          },
          // Succès : `useUnlockCounter` remplace l'utilisateur courant, l'App
          // rebascule sur l'interface complète et cette fenêtre disparaît avec.
        }
      );
    }
  }

  // Le pavé se tape aussi au clavier : au comptoir, la souris n'est pas
  // toujours la plus rapide.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') press(e.key);
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"
          title="Fermer"
        >
          <X size={16} />
        </button>

        <div className="text-center mb-5">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-2.5">
            <Lock size={18} className="text-slate-600" />
          </div>
          <div className="text-[15px] font-semibold">Accès gestion</div>
          <div className="text-[12px] text-slate-500 mt-0.5">
            Code PIN du titulaire
          </div>
        </div>

        <div className="flex justify-center gap-2.5 mb-5">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-11 h-12 rounded-xl border-2 flex items-center justify-center text-lg ${
                error
                  ? 'border-red-300 bg-red-50'
                  : pin.length > i
                    ? 'border-accent bg-slate-50'
                    : 'border-slate-200'
              }`}
            >
              {pin.length > i ? '●' : ''}
            </div>
          ))}
        </div>

        {error && (
          <div className="text-center text-[12px] text-red-600 mb-3">{error}</div>
        )}
        {unlock.isPending && (
          <div className="text-center text-[12px] text-slate-500 mb-3">
            Vérification…
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button
              key={d}
              onClick={() => press(d)}
              className="h-12 rounded-xl bg-slate-100 hover:bg-slate-200 text-[17px] font-medium"
            >
              {d}
            </button>
          ))}
          <div />
          <button
            onClick={() => press('0')}
            className="h-12 rounded-xl bg-slate-100 hover:bg-slate-200 text-[17px] font-medium"
          >
            0
          </button>
          <button
            onClick={() => setPin((p) => p.slice(0, -1))}
            className="h-12 rounded-xl hover:bg-slate-100 text-slate-500 flex items-center justify-center"
            title="Effacer"
          >
            <Delete size={17} />
          </button>
        </div>

        <p className="text-[10.5px] text-slate-400 text-center mt-4 leading-relaxed">
          La gestion se referme d'elle-même au bout de 30 minutes,
          <br />
          ou après 5 minutes sans activité.
        </p>
      </div>
    </div>
  );
}

export default function CounterLayout() {
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const [pinOpen, setPinOpen] = useState(false);
  const viewer = !!user?.email;

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 20_000);
    return () => clearInterval(t);
  }, []);

  const timeLabel = now
    .toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    .replace(':', 'h');
  const dateLabel = now.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className="w-full h-screen bg-slate-100 flex flex-col overflow-hidden">
      <header
        className="flex items-center gap-6 px-5 py-2.5 border-b border-white/10"
        style={{ backgroundColor: '#0f1f35' }}
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
            <Pill size={17} color="#fff" strokeWidth={2.25} />
          </div>
          <div>
            <div className="text-[15px] font-semibold tracking-tight text-white leading-tight">
              Officina
            </div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 -mt-0.5">
              {user?.pharmacy?.name ?? 'Poste comptoir'}
            </div>
          </div>
        </div>

        <nav className="flex items-center gap-1 flex-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-lg text-[13.5px] transition-colors ${
                    isActive
                      ? 'bg-white/10 text-white font-medium'
                      : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.75} />
                <span>{t.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="text-right">
          <div className="text-[17px] font-semibold tabular-nums leading-none text-white">
            {timeLabel}
          </div>
          <div className="text-[10.5px] text-white/50 capitalize mt-0.5">{dateLabel}</div>
        </div>

        {viewer ? (
          <>
            <div
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 text-[11px] text-white/80"
              title={user?.email ?? undefined}
            >
              <Eye size={12} />
              Consultation
            </div>
            <button
              onClick={logout}
              title="Se déconnecter"
              className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10"
            >
              <LogOut size={15} />
            </button>
          </>
        ) : (
          <button
            onClick={() => setPinOpen(true)}
            title="Accès gestion (titulaire)"
            className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10"
          >
            <Lock size={15} />
          </button>
        )}
      </header>

      <main className="flex-1 bg-white overflow-hidden flex flex-col">
        <Outlet />
      </main>

      {pinOpen && <PinPad onClose={() => setPinOpen(false)} />}
    </div>
  );
}
